/**
 * GraphQL Hooks for Sharing
 *
 * Apollo Client hooks for sharing-related queries.
 * Uses the sharing GraphQL schema from Feature 080.
 *
 * Feature: 080-critique-sharing
 * Tasks: T020
 */

import { useQuery, useMutation, gql } from '@apollo/client';
import logger from '../../utils/logger';

// ============================================================================
// GraphQL Queries
// ============================================================================

/**
 * Query to get sharing info for a critique session
 */
const GET_CRITIQUE_SESSION_SHARE_INFO = gql`
  query GetCritiqueSessionShareInfo($critiqueSessionId: ID!) {
    critiqueSessionShareInfo(critiqueSessionId: $critiqueSessionId) {
      shareUrl
      canShare
      isOwner
      critiqueSessionId
      collectionId
      collectionTitle
      ownerUsername
    }
  }
`;

/**
 * Query to validate a share token from URL
 */
const VALIDATE_SHARE_TOKEN = gql`
  query ValidateShareToken($token: String!) {
    validateShareToken(token: $token) {
      isValid
      errorCode
      errorMessage
      critiqueSessionId
      collectionId
      collectionTitle
      collectionSlug
      ownerUsername
      usesRemaining
    }
  }
`;

/**
 * Query to get recent contacts for MRU list in share dialog
 */
const GET_RECENT_CONTACTS = gql`
  query GetRecentContacts($limit: Int) {
    recentContacts(limit: $limit) {
      userId
      email
      username
      displayName
      avatarUrl
      isPending
      lastInteractionAt
      interactionCount
    }
  }
`;

// ============================================================================
// GraphQL Mutations
// ============================================================================

/**
 * Mutation to consume a share token (increment use count)
 */
const CONSUME_SHARE_TOKEN = gql`
  mutation ConsumeShareToken($token: String!) {
    consumeShareToken(token: $token)
  }
`;

/**
 * Mutation to re-invite a pending share recipient
 */
const RE_INVITE_RECIPIENT = gql`
  mutation ReInviteRecipient($shareEventId: ID!) {
    reInviteRecipient(shareEventId: $shareEventId) {
      success
      message
      shareUrl
      emailStatus
    }
  }
`;

// ============================================================================
// Types
// ============================================================================

export interface ShareLinkInfo {
  shareUrl: string;
  canShare: boolean;
  isOwner: boolean;
  critiqueSessionId: string;
  collectionId: string;
  collectionTitle: string;
  ownerUsername: string;
}

export interface ValidShareToken {
  isValid: boolean;
  errorCode: string | null;
  errorMessage: string | null;
  critiqueSessionId: string | null;
  collectionId: string | null;
  collectionTitle: string | null;
  collectionSlug: string | null;
  ownerUsername: string | null;
  usesRemaining: number;
}

export interface EngagementContact {
  userId: string | null;
  email: string | null;
  username: string | null;
  displayName: string | null;
  avatarUrl: string | null;
  isPending: boolean;
  lastInteractionAt: string;
  interactionCount: number;
}

interface GetShareInfoData {
  critiqueSessionShareInfo: ShareLinkInfo;
}

interface ValidateShareTokenData {
  validateShareToken: ValidShareToken;
}

interface GetRecentContactsData {
  recentContacts: EngagementContact[];
}

interface ConsumeShareTokenData {
  consumeShareToken: boolean;
}

export interface ReInviteResult {
  success: boolean;
  message: string;
  shareUrl: string | null;
  emailStatus: 'pending' | 'sent' | 'failed' | null;
}

interface ReInviteRecipientData {
  reInviteRecipient: ReInviteResult;
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Query: Get sharing info for a critique session
 *
 * Returns whether the current user can share the session,
 * and the base share URL if they can.
 *
 * @param critiqueSessionId - Global ID of the critique session
 */
export function useCritiqueSessionShareInfoGraphQL(critiqueSessionId: string | undefined) {
  const { data, loading, error, refetch } = useQuery<GetShareInfoData>(
    GET_CRITIQUE_SESSION_SHARE_INFO,
    {
      // CRITICAL: Always provide placeholder values when skipping to prevent Apollo Client
      // from validating required variables before checking skip.
      variables: { critiqueSessionId: critiqueSessionId || 'skip-placeholder' },
      skip: !critiqueSessionId,
      fetchPolicy: 'cache-and-network',
    }
  );

  return {
    data: data?.critiqueSessionShareInfo || null,
    isLoading: loading,
    isError: !!error,
    error,
    refetch,
  };
}

/**
 * Query: Validate a share token from URL
 *
 * Used when a user visits a share link to check if it's valid
 * and get information about the shared content.
 *
 * @param token - The share token from the URL (64 hex characters)
 */
export function useValidateShareTokenGraphQL(token: string | undefined) {
  const { data, loading, error, refetch } = useQuery<ValidateShareTokenData>(
    VALIDATE_SHARE_TOKEN,
    {
      // CRITICAL: Always provide placeholder values when skipping to prevent Apollo Client
      // from validating required variables before checking skip.
      variables: { token: token || 'skip-placeholder' },
      skip: !token,
      fetchPolicy: 'network-only', // Always validate fresh from server
    }
  );

  if (data) {
    logger.debug('Sharing', 'Share token validation result', {
      isValid: data.validateShareToken.isValid,
      errorCode: data.validateShareToken.errorCode,
    });
  }

  return {
    data: data?.validateShareToken || null,
    isLoading: loading,
    isError: !!error,
    error,
    refetch,
  };
}

/**
 * Query: Get recent contacts for MRU list
 *
 * Returns contacts sorted by most recent interaction,
 * useful for the share dialog's quick-select list.
 *
 * @param limit - Maximum number of contacts to return (default: 15)
 */
export function useRecentContactsGraphQL(limit: number = 15) {
  const { data, loading, error, refetch } = useQuery<GetRecentContactsData>(
    GET_RECENT_CONTACTS,
    {
      variables: { limit },
      fetchPolicy: 'cache-and-network',
    }
  );

  return {
    data: data?.recentContacts || [],
    isLoading: loading,
    isError: !!error,
    error,
    refetch,
  };
}

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Mutation: Consume a share token
 *
 * Call this when a user accesses shared content via a share token.
 * Increments the use count on the token.
 *
 * @returns Function to consume the token, plus loading/error state
 */
export function useConsumeShareTokenGraphQL() {
  const [mutate, { loading, error }] = useMutation<ConsumeShareTokenData>(
    CONSUME_SHARE_TOKEN
  );

  const consumeToken = async (token: string): Promise<boolean> => {
    try {
      const result = await mutate({ variables: { token } });
      const consumed = result.data?.consumeShareToken ?? false;

      logger.debug('Sharing', 'Share token consumed', { consumed });

      return consumed;
    } catch (err) {
      logger.error('Sharing', 'Failed to consume share token', {
        error: err instanceof Error ? err.message : String(err),
      });
      return false;
    }
  };

  return {
    consumeToken,
    isLoading: loading,
    isError: !!error,
    error,
  };
}

/**
 * Mutation: Re-invite a pending share recipient
 *
 * Creates a new share token and sends another invitation email
 * to a recipient who hasn't registered yet.
 *
 * @returns Function to re-invite, plus loading/error state
 */
export function useReInviteRecipientGraphQL() {
  const [mutate, { loading, error }] = useMutation<ReInviteRecipientData>(
    RE_INVITE_RECIPIENT
  );

  const reInvite = async (shareEventId: string): Promise<ReInviteResult> => {
    try {
      const result = await mutate({ variables: { shareEventId } });
      const response = result.data?.reInviteRecipient;

      if (response) {
        logger.info('Sharing', 'Re-invite result', {
          success: response.success,
          emailStatus: response.emailStatus,
        });
        return response;
      }

      return {
        success: false,
        message: 'No response from server',
        shareUrl: null,
        emailStatus: null,
      };
    } catch (err) {
      logger.error('Sharing', 'Failed to re-invite recipient', {
        error: err instanceof Error ? err.message : String(err),
      });
      return {
        success: false,
        message: err instanceof Error ? err.message : 'Failed to re-invite',
        shareUrl: null,
        emailStatus: null,
      };
    }
  };

  return {
    reInvite,
    isLoading: loading,
    isError: !!error,
    error,
  };
}
